/**
 * Upload queue type definitions
 */

export type UploadStatus =
  | 'processing'  // Image being converted/dithered
  | 'queued'      // Waiting to upload
  | 'uploading'   // Currently uploading
  | 'complete'    // Successfully uploaded
  | 'failed'      // Upload failed
  | 'paused'      // Paused by user

export interface UploadItem {
  id: string                    // Unique identifier (UUID)
  googleId?: string             // Google Photos ID (if from Google Photos)
  sourceUrl: string             // Original image URL/blob URL
  filename: string              // Target filename (e.g., "01-11_2026_abc123.bmp")
  date: string                  // Target date in MM-DD format
  status: UploadStatus
  progress: number              // 0-100
  previewUrl?: string           // Thumbnail blob URL
  processedData?: Uint8Array    // Processed BMP data (stored in IndexedDB)
  error?: string                // Error message if failed
  retryCount: number            // Number of retry attempts
  createdAt: Date
  completedAt?: Date
}

export interface UploadState {
  queue: UploadItem[]
  isPaused: boolean
  currentUploadId: string | null
}

export type UploadAction =
  | { type: 'ADD_ITEMS'; items: UploadItem[] }
  | { type: 'REMOVE_ITEM'; id: string }
  | { type: 'UPDATE_ITEM'; id: string; updates: Partial<UploadItem> }
  | { type: 'SET_CURRENT'; id: string | null }
  | { type: 'PAUSE_ALL' }
  | { type: 'RESUME_ALL' }
  | { type: 'CLEAR_ALL' }
  | { type: 'CLEAR_COMPLETED' }
  | { type: 'RETRY_FAILED' }

// Generate filename in format: MM-DD_YYYY_googleId.bmp
export function generateFilename(date: Date, googleId?: string): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  const suffix = googleId ? `_${googleId.slice(0, 10)}` : ''
  return `${month}-${day}_${year}${suffix}.bmp`
}
